import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["report", "validationMode", "onReportResolve", "onError", "visualizationDebug", "isMobile", "noDataRenderer"];
import { useContext, useEffect, useMemo, useState } from 'react';
import { fromJS, List } from 'immutable';
import { isConfigReport, isRelationalReport } from 'reporting-data/tsTypes/reportTypes';
import { buildVisualization, attachEvents } from './buildVisualization';

// Snowflake
import { createRelationalReportFromBaseReport } from 'reporting-snowflake/relational/utils/report-utils';
import { getRelationalReportDetails, getConfigReportDetails } from './DetailsFromReport';
import Visualization from 'reporting-visualizations/Visualization';
import { useResolveData } from './useResolveData';
import { getData } from 'reporting-data/v2/dataset/datasetRecords';
import { useFetchUnifiedBrandKits } from './useFetchUnifiedBrandKits';
import { UnifiedVizValidationContext } from './UnifiedVizValidationContextProvider';
import useUpdatingRef from '../../shared/hooks/useUpdatingRef';
import { onReportResolvePayloadConfigReport } from '../../../reports/utils/reportResolvePayloadUtil';
// @ts-expect-error UnTyped dependency
import ReportErrored from '../../../reports/inferLogic/components/states/ReportErrored';
import { getChart } from 'reporting-data/report/reportGetters';
//@ts-expect-error Untyped import
import ReportLoading from '../../../reports/inferLogic/components/states/ReportLoading';
import NoData from '../../shared/NoData';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
export const isUnifiedResolveSupportedReportType = report => isConfigReport(report) || isRelationalReport(report);
export const getReportDetails = report => {
  if (isConfigReport(report)) {
    return getConfigReportDetails(report);
  } else if (isRelationalReport(report)) {
    return getRelationalReportDetails(createRelationalReportFromBaseReport(report));
  }
  return {};
};
const useResolveUnifiedReportVisualization = ({
  report,
  onReportResolve,
  validationMode,
  isMobile
}) => {
  const {
    areBrandKitsLoading,
    brandKitPalettes
  } = useFetchUnifiedBrandKits(report);
  const [unifiedReportResolveError, setUnifiedReportResolveError] = useState();

  // Fetch the data for the report
  const {
    data: dataset,
    metaStateData,
    loading: dataResolveLoading,
    error: dataResolveError
  } = useResolveData({
    report
  });
  useMemo(() => {
    if (dataResolveError) {
      setUnifiedReportResolveError(dataResolveError);
    }
  }, [dataResolveError]);

  // stabilize the onReportResolve function, otherwise this will get updated on every render
  const onReportResolveRef = useUpdatingRef(onReportResolve);
  useEffect(() => {
    if (dataset && onReportResolveRef.current && !validationMode) {
      if (isRelationalReport(report)) {
        onReportResolveRef.current(dataset);
      } else {
        onReportResolveRef.current(onReportResolvePayloadConfigReport({
          report,
          dataset
        }));
      }
    }
  }, [dataset, report, onReportResolveRef, validationMode]);
  const unifiedReportVisualization = useMemo(() => {
    try {
      if (!dataset || unifiedReportResolveError !== undefined) {
        return undefined;
      }
      const primaryDataset = dataset.get('primary');
      const data = getData(primaryDataset) || List([]);
      if (data.isEmpty()) {
        return undefined;
      }
      if (!isUnifiedResolveSupportedReportType(report)) {
        throw new Error('Unsupported Report Type in unifiedReportVisualization');
      }
      const chart = getChart(report);

      // get report detail such as metrics, dimensions, ...
      const reportDetail = getReportDetails(report);

      // build the visualization object
      let buildResult = buildVisualization({
        datasetMap: dataset,
        chart: (chart !== null && chart !== void 0 ? chart : fromJS({})).toJS(),
        reportDetail,
        brandKitPalettes,
        originalReport: report,
        isMobile
      });

      // attaching events (such as onSort or onClick for the drilldown) to the visualization objects
      buildResult = attachEvents({
        report,
        visualization: buildResult,
        dataset,
        metaStateData
      });
      return buildResult;
    } catch (e) {
      setUnifiedReportResolveError(e);
      return undefined;
    }
  }, [dataset, unifiedReportResolveError, report, brandKitPalettes, metaStateData, isMobile]);
  return {
    unifiedReportVisualization,
    resolveLoading: (dataResolveLoading || areBrandKitsLoading) && unifiedReportResolveError === undefined,
    unifiedReportResolveError
  };
};
export const UnifiedReportResolver = _ref => {
  let {
      report,
      validationMode = false,
      onReportResolve,
      onError,
      visualizationDebug,
      isMobile = false,
      noDataRenderer
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    unifiedReportVisualization,
    resolveLoading,
    unifiedReportResolveError
  } = useResolveUnifiedReportVisualization({
    report,
    onReportResolve,
    onError,
    validationMode,
    isMobile
  });
  const {
    setUnifiedVisualization,
    setUnifiedReportResolveError
  } = useContext(UnifiedVizValidationContext);
  const onReportErrorRef = useUpdatingRef(onError);
  useEffect(() => {
    try {
      if (validationMode) {
        if (!resolveLoading && unifiedReportVisualization !== undefined) {
          setUnifiedVisualization(unifiedReportVisualization);
        }
        if (unifiedReportResolveError !== undefined) {
          setUnifiedReportResolveError(unifiedReportResolveError);
        }
      } else {
        // if not in the validation mode, if there is an error
        // we need to call the onError callback
        if (unifiedReportResolveError) {
          var _onReportErrorRef$cur;
          (_onReportErrorRef$cur = onReportErrorRef.current) === null || _onReportErrorRef$cur === void 0 || _onReportErrorRef$cur.call(onReportErrorRef, unifiedReportResolveError);
        }
      }
    } catch (e) {
      console.error('Error setting Unified Report Resolver resolve context', e);
    }
  }, [onReportErrorRef, resolveLoading, setUnifiedReportResolveError, setUnifiedVisualization, unifiedReportResolveError, unifiedReportVisualization, validationMode]);
  if (validationMode) {
    return null;
  }
  if (resolveLoading) {
    return /*#__PURE__*/_jsx(ReportLoading, {});
  }
  if (unifiedReportVisualization) {
    return /*#__PURE__*/_jsx(Visualization, Object.assign({
      visualization: unifiedReportVisualization,
      debug: visualizationDebug
    }, props));
  }
  if (unifiedReportResolveError) {
    return /*#__PURE__*/_jsx(ReportErrored, {
      error: unifiedReportResolveError,
      isMobile: isMobile
    });
  }
  return noDataRenderer ? noDataRenderer() : /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    flexGrow: 1,
    "data-status-id": "report-status-loaded",
    "data-test-id": "reporting-visualization-report",
    children: /*#__PURE__*/_jsx(NoData, {
      isMobile: isMobile,
      visualizationDebug: visualizationDebug
    })
  });
};